import React, { createContext, useContext } from 'react'
import { WorkflowGraphValidationIssue } from './workflowSaveErrorUtils'

export interface GraphValidationContextValue {
  issues: WorkflowGraphValidationIssue[]
  nodeIssues: Map<string, WorkflowGraphValidationIssue[]>
  edgeIssues: Map<string, WorkflowGraphValidationIssue[]>
}

const GraphValidationContext = createContext<GraphValidationContextValue>({
  issues: [],
  nodeIssues: new Map(),
  edgeIssues: new Map()
})

const buildIssueMap = (
  issues: WorkflowGraphValidationIssue[],
  key: 'nodeId' | 'edgeId'
): Map<string, WorkflowGraphValidationIssue[]> => {
  const map = new Map<string, WorkflowGraphValidationIssue[]>()

  issues.forEach(issue => {
    const id = issue[key]
    if (!id) {
      return
    }

    const existing = map.get(id) ?? []
    existing.push(issue)
    map.set(id, existing)
  })

  return map
}

export const GraphValidationProvider: React.FC<{
  issues: WorkflowGraphValidationIssue[]
  children: React.ReactNode
}> = ({ issues, children }) => {
  const value: GraphValidationContextValue = {
    issues,
    nodeIssues: buildIssueMap(issues, 'nodeId'),
    edgeIssues: buildIssueMap(issues, 'edgeId')
  }

  return <GraphValidationContext.Provider value={value}>{children}</GraphValidationContext.Provider>
}

export const useGraphValidation = (): GraphValidationContextValue =>
  useContext(GraphValidationContext)

export const useNodeValidationIssues = (nodeId: string): WorkflowGraphValidationIssue[] => {
  const { nodeIssues } = useGraphValidation()
  return nodeIssues.get(nodeId) ?? []
}
